import React, { useState, useEffect } from 'react';
import * as consts from 'config/consts';
import Loading from 'components/commons/loading/Loading';
import { useRouting } from 'hooks/useRouting';
import {
  getPositions,
  getNotificationSetting,
  GetNotificationSettingResult,
  updateNotificationSetting,
  UpdateNotificationSetting,
} from 'config/api';

type SelectedSymbols = {
  stock: string[];
  // NOTE: 対象商品が追加された場合は下記のように追記する。
  // foregin: string[];
};

type HoldPositionProps = {
  symbol: string;
  symbolName: string;
};

const TimelyDisclosureList = ({
  holdPositions,
  notificationSetting,
  registNotificationSetting,
}: {
  holdPositions: HoldPositionProps[];
  notificationSetting: GetNotificationSettingResult;
  registNotificationSetting: (selectedSymbols: SelectedSymbols) => void;
}): React.ReactElement => {
  const routing = useRouting();

  const initialSelectedSymbols = () => {
    return {
      stock: notificationSetting.TimelyDisclosureBySymbol,
    };
  };
  const initialSymbols = initialSelectedSymbols();
  const [selectedSymbols, setSelectedSymbols] = useState<SelectedSymbols>(
    initialSymbols
  );

  const toggleAll = (): void => {
    holdPositions.length !== selectedSymbols.stock.length
      ? setSelectedSymbols({
          stock: holdPositions.map(position => position.symbol),
        })
      : setSelectedSymbols({ stock: [] });
  };

  const toggleStockAll = (): void => {
    const symbols = holdPositions.map(position => position.symbol);
    holdPositions.length !== selectedSymbols.stock.length
      ? setSelectedSymbols({ stock: symbols })
      : setSelectedSymbols({ stock: [] });
  };

  const regist = async () => {
    await registNotificationSetting(selectedSymbols);
    routing.backRouting(true);
  };

  return (
    <div className={`py-4 flex flex-col bg-white`}>
      <div className={`px-4 text-sm`}>
        適時開示情報を受け取る銘柄を選択してください
      </div>
      <div className={`px-4 mt-6`}>
        <div
          className="flex flex-row items-center cursor-pointer"
          onClick={(): void => toggleAll()}>
          <div
            className={`icon-check text-white rounded-sm ${
              holdPositions.length === selectedSymbols.stock.length
                ? 'bg-blue-700'
                : 'bg-bluegray-300'
            }`}></div>
          <div
            className={`flex flex-row items-center ml-4 text-base font-bold`}>
            全銘柄一括選択
          </div>
        </div>
      </div>
      <div className={`px-4 flex flex-row mt-6 mb-2`}>
        <div className={`text-base font-bold`}>国内株式</div>
        <div
          className={`flex flex-row items-center ml-auto cursor-pointer`}
          onClick={(): void => toggleStockAll()}>
          <div
            className={`icon-check text-white rounded-sm ${
              holdPositions.length === selectedSymbols.stock.length
                ? 'bg-blue-700'
                : 'bg-bluegray-300'
            }`}></div>
          <div className={`ml-2 text-sm`}>国内株式を一括選択</div>
        </div>
      </div>
      {holdPositions.map((position, i) => {
        return (
          <TimelyDisclosureItem
            {...{
              item: position,
              key: i,
              isSelected: selectedSymbols.stock.includes(position.symbol),
              toggle: (): void =>
                setSelectedSymbols((prev): typeof prev => {
                  const symbols = prev.stock.includes(position.symbol)
                    ? prev.stock.filter(item => item !== position.symbol)
                    : [position.symbol, ...prev.stock];
                  return { ...prev, stock: symbols };
                }),
            }}
          />
        );
      })}
      <div className={`border-bluegray-300 border-b`}></div>

      <button
        className={`mx-4 mt-8 bg-tradewell rounded-lg shadow text-white py-2 text-center flex flex-row items-center gap-2 justify-center text-base font-bold`}
        onClick={() => regist()}>
        決定
      </button>
      <button
        className={`mx-4 mt-4 p-2 rounded-lg border border-bluegray-400 text-bluegray-700 bg-gray-50 font-bold flex flex-row items-center justify-center text-base`}
        onClick={(): void => routing.backRouting(true)}>
        戻る
      </button>
    </div>
  );
};

const TimelyDisclosureItem = ({
  item,
  isSelected,
  toggle,
}: {
  item: HoldPositionProps;
  isSelected: boolean;
  toggle: () => void;
}): React.ReactElement => {
  return (
    <div
      className={`bg-bluegray-50 border-bluegray-300 border-t p-2 cursor-pointer`}
      onClick={(): void => toggle()}>
      <div className={`flex flex-row items-center ml-6`}>
        <div
          className={`icon-check text-white rounded-sm ${
            isSelected ? 'bg-blue-700' : 'bg-bluegray-300'
          }`}></div>

        <div className={`flex flex-col ml-4`}>
          <div className={`text-sm text-bluegray-500 mb-1`}>{item.symbol}</div>
          <div className={`font-bold `}>{item.symbolName}</div>
        </div>
      </div>
    </div>
  );
};

export const TimelyDisclosure: React.FC = () => {
  const [holdPositions, setHoldPositions] = useState<HoldPositionProps[]>();
  const [notificationSetting, setNotificationSetting] = useState<
    GetNotificationSettingResult | undefined
  >();

  useEffect(() => {
    (async () => {
      const positionsResponse = await getPositions();

      // memo: MK_TW2-1092の対応で国内ETF・ETNも設定可能としたため、国内株式に含めて表示させる。
      const stockOnlyLists = positionsResponse.Items.filter(
        items =>
          (items.ProductType === consts.productTypes.国内株式 ||
            items.ProductType === consts.productTypes['国内ETF・ETN']) &&
          items.BrokerID !== consts.ManualBrokerID.手入力口座
      ).map(item => {
        return {
          symbol: item.Symbol,
          symbolName: item.SymbolName,
        };
      });

      const uniquePositions = stockOnlyLists.filter(
        (element, index, self) =>
          self.findIndex(e => e.symbol === element.symbol) === index
      ) as HoldPositionProps[];
      setHoldPositions(uniquePositions);

      const notificationSettingResponse = await getNotificationSetting();
      setNotificationSetting(notificationSettingResponse);
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!holdPositions || holdPositions === undefined || !notificationSetting)
    return (
      <Loading
        loading={true}
        style={{
          backgroundColor: '#edf2f7',
          alignItems: 'normal',
          paddingTop: '200px',
          top: '95',
          left: '0',
        }}
      />
    );

  const registNotificationSetting = async (
    selectedSymbols: SelectedSymbols
  ) => {
    const symbols = Object.values(selectedSymbols.stock);
    // TODO: 不正ななテストデータが来た時用　マージの際に削除する
    // const list = symbols.filter(
    //   symbol => Number(symbol) > 999 && Number(symbol) < 10000
    // );
    const prams = {
      TimelyDisclosureBySymbol: symbols,
      AssetFluctuationThresholdPer:
        notificationSetting.AssetFluctuationThresholdPer,
      EconomicIndicatorsOnToday: notificationSetting.EconomicIndicatorsOnToday,
      NewDepositAndWithdrawal: notificationSetting.NewDepositAndWithdrawal,
      NewTrade: notificationSetting.NewTrade,
    } as UpdateNotificationSetting;

    await updateNotificationSetting(prams);
  };

  return (
    <TimelyDisclosureList
      {...{
        holdPositions,
        notificationSetting,
        registNotificationSetting,
      }}
    />
  );
};

export default TimelyDisclosure;
